import { Reference, useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";

import ActionIconButton from "@components/buttons/ActionIconButton";
import AsyncSelect, { Option } from "@components/fields/AsyncSelect";
import BaseErrors from "@components/fields/BaseErrors";
import BooleanRadioButtonGroup from "@components/fields/BooleanRadioButtonGroup";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import PhoneInput from "@components/fields/PhoneInput";
import RichTextarea from "@components/fields/RichTextarea";
import Form from "@components/forms/Form";
import styles from "@components/forms/ResourceForm.module.css";
import Heading3 from "@components/typography/Heading3";
import useAsyncSearch from "@hooks/useAsyncSearch";
import useBooleanToggle from "@hooks/useBooleanToggle";
import useFormPersist from "@hooks/useFormPersist";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import {
  GetHotlinesDocument,
  GetHotlinesQuery,
  GetHotlinesQueryVariables,
  GetJourneyWithClientVisibleTasksQuery,
  Hotline,
  ResourceFragment as Resource,
  ResourceCreateDocument,
  ResourceCreateMutation,
  ResourceCreateMutationVariables,
  ResourceFeatureKindEnum,
  ResourceFragmentDoc,
  ResourceUpdateDocument,
  ResourceUpdateMutation,
  ResourceUpdateMutationVariables,
  TaskFragment as Task
} from "@typing/Generated";
import { handleMutationResponse, standardRichTextareaMenuControls } from "@utils/formUtils";
import { optionsForBoolean } from "@utils/optionUtils";

type FormData = {
  cost: string | null;
  description: string;
  featureValues: { value: string | boolean | null }[];
  hotlineId: string | null;
  linkTitle: string | null;
  linkUrl: string | null;
  phoneNumber: string | null;
  taskId: string | null;
  title: string;
};

type Props = {
  autofocus: boolean;
  journey: GetJourneyWithClientVisibleTasksQuery["journey"];
  onCancel: () => void;
  onDelete: () => void;
  onSuccess: (editing: boolean) => void;
  resource?: Resource | null;
  task: Task;
};

type Feature = Task["resourceFeatures"][number];

type ResourceFeatureValueInput = {
  booleanValue: boolean | null;
  resourceFeatureId: string;
  textValue: string | null;
};

const ResourceForm = ({ autofocus, journey, onCancel, onSuccess, resource, task }: Props) => {
  const editorRefresher = useBooleanToggle();

  const valueForFeature = useCallback(
    (feature: Feature) => {
      if (resource) {
        const featureValue = resource.featureValues.find(value => value.resourceFeature.id === feature.id);
        if (featureValue) {
          if (feature.kind === ResourceFeatureKindEnum.BOOLEAN) {
            return featureValue.booleanValue;
          }
          return featureValue.textValue;
        }
      }
      return null;
    },
    [resource]
  );

  const calculateDefaultValues = useCallback(
    (): FormData => ({
      cost: resource?.cost ?? null,
      description: resource?.description ?? "",
      featureValues: task.resourceFeatures.map(feature => ({
        value: valueForFeature(feature)
      })),
      hotlineId: resource?.hotline?.id ?? null,
      linkTitle: resource?.linkTitle ?? null,
      linkUrl: resource?.linkUrl ?? null,
      phoneNumber: resource?.phoneNumber ?? null,
      taskId: resource?.task.id ?? task?.id ?? null,
      title: resource?.title ?? ""
    }),
    [
      resource?.cost,
      resource?.description,
      resource?.hotline?.id,
      resource?.linkTitle,
      resource?.linkUrl,
      resource?.phoneNumber,
      resource?.task.id,
      resource?.title,
      task?.id,
      task.resourceFeatures,
      valueForFeature
    ]
  );

  const defaultValues = calculateDefaultValues();

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createResource] = useMutation<ResourceCreateMutation, ResourceCreateMutationVariables>(
    ResourceCreateDocument,
    {
      update: (store, { data }) => {
        if (data?.resourceCreate?.resource) {
          const newResource = data.resourceCreate.resource;
          store.modify({
            fields: {
              resources(cachedResourcesRefs: readonly Reference[]) {
                const newResourceRef = store.writeFragment({
                  data: newResource,
                  fragment: ResourceFragmentDoc,
                  fragmentName: "Resource"
                });
                if (newResourceRef) {
                  return [...cachedResourcesRefs, newResourceRef];
                }
                return [...cachedResourcesRefs];
              }
            },
            id: store.identify(task)
          });
        }
      }
    }
  );

  const [updateResource] = useMutation<ResourceUpdateMutation, ResourceUpdateMutationVariables>(ResourceUpdateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      cost: yup.string().nullable().defined(),
      description: yup.string().required(intl.formatMessage({ id: "errors.resource.description.blank" })),
      featureValues: yup
        .array()
        .defined()
        .of(
          yup.object().shape({
            value: yup.string().nullable().defined()
          })
        ),
      hotlineId: yup.string().nullable().defined(),
      linkTitle: yup.string().nullable().defined(),
      linkUrl: yup.lazy(value => {
        if (!value) {
          return yup.string().nullable().defined();
        }
        return yup
          .string()
          .nullable()
          .defined()
          .url(intl.formatMessage({ id: "errors.resource.linkUrl.formatting" }));
      }),
      phoneNumber: yup.string().nullable().defined(),
      taskId: yup.string().required(intl.formatMessage({ id: "errors.resource.taskId.blank" })),
      title: yup.string().required(intl.formatMessage({ id: "errors.resource.title.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const formPersist = useFormPersist("resourceForm", {
    form,
    onDataRestored: () => {
      editorRefresher.toggle();
    },
    uniqueId: `${task.id} ${resource?.id}`
  });

  useEffect(() => {
    form.reset(calculateDefaultValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateDefaultValues]);

  const linkTitle = form.watch("linkTitle");
  const linkUrl = form.watch("linkUrl");
  const phoneNumber = form.watch("phoneNumber");

  const phoneNumberValid = useMemo(() => {
    if (phoneNumber && phoneNumber !== "") {
      return isValidPhoneNumber(phoneNumber);
    } else {
      return true;
    }
  }, [phoneNumber]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    if ((linkTitle || linkUrl) && (!linkTitle || !linkUrl)) {
      if (!linkTitle) {
        form.setError("linkTitle", {
          message: intl.formatMessage({ id: "errors.resource.linkTitle.blank" }),
          type: "validate"
        });
      }
      if (!linkUrl) {
        form.setError("linkUrl", {
          message: intl.formatMessage({ id: "errors.resource.linkUrl.blank" }),
          type: "validate"
        });
      }
      return;
    }

    const values = form.getValues();

    const mappedFeatureValues = values.featureValues.map((featureValue, index) => {
      const feature = task.resourceFeatures[index];

      const result: ResourceFeatureValueInput = {
        booleanValue: null,
        resourceFeatureId: feature.id,
        textValue: null
      };
      if (feature.kind === ResourceFeatureKindEnum.BOOLEAN) {
        result.booleanValue = featureValue.value as boolean | null;
      } else {
        result.textValue = featureValue.value as string | null;
      }
      return result;
    });

    const newValues = {
      ...values,
      featureValues: mappedFeatureValues
    };

    if (resource) {
      return updateResource({
        variables: {
          ...newValues,
          id: resource.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.resourceUpdate,
          intl,
          onSuccess: () => {
            formPersist.clear();
            form.reset();
            editorRefresher.toggle();
            onSuccess(true);
          },
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.resource"
        });
      });
    }
    return createResource({
      variables: {
        ...newValues,
        taskId: form.getValues().taskId ?? ""
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.resourceCreate,
        intl,
        onSuccess: () => {
          formPersist.clear();
          form.reset();
          editorRefresher.toggle();
          onSuccess(false);
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.resource"
      });
    });
  }, [
    createResource,
    editorRefresher,
    form,
    formPersist,
    intl,
    linkTitle,
    linkUrl,
    onSuccess,
    resource,
    task.resourceFeatures,
    updateResource
  ]);

  useEffect(() => {
    if (phoneNumber && !phoneNumberValid) {
      form.setError("phoneNumber", {
        message: intl.formatMessage({ id: "errors.resource.phoneNumber.invalid" }),
        type: "validate"
      });
    }
  }, [form, intl, phoneNumber, phoneNumberValid]);

  useEffect(() => {
    editorRefresher.toggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource?.id]);

  const { fields: resourceFeatureFields } = useFieldArray<FormData>({
    control: form.control,
    name: "featureValues"
  });

  const handleCancel = useCallback(() => {
    form.reset(defaultValues);
    form.clearErrors();
    formPersist.clear();
    editorRefresher.toggle();
    onCancel();
  }, [editorRefresher, form, formPersist, defaultValues, onCancel]);

  const generateLabelFromHotline = (hotline?: Resource["hotline"] | null): Option["label"] =>
    hotline ? `${hotline.phoneNumber} - ${hotline.name}` : "";

  const initialHotlineOption = useMemo(
    () => ({
      label: generateLabelFromHotline(resource?.hotline),
      value: resource?.hotline?.id ?? ""
    }),
    [resource?.hotline]
  );

  const hotlineAsyncSearch = useAsyncSearch<Hotline>({
    defaultOptions: [],
    getLabel: h => generateLabelFromHotline(h),
    getValue: h => (h ? h.id : ""),
    initialOption: initialHotlineOption,
    loadOptionsOnOpen: true
  });

  useEffect(() => {
    hotlineAsyncSearch.setCurrentOption(initialHotlineOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRefresher]);

  const getHotline = useLazyQueryWithPromise<GetHotlinesQuery, GetHotlinesQueryVariables>(GetHotlinesDocument);

  return (
    <Form data-test="resource-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className={styles.flex}>
        <Heading3>
          <FormattedMessage id="forms.resource.basicInformation" />
        </Heading3>

        <ActionIconButton
          ariaLabelKey="accessibility.ariaLabels.close"
          icon="times"
          onClick={handleCancel}
          size="medium"
        />
      </div>

      <Input<FormData>
        autofocus={autofocus}
        error={form.formState.errors.title}
        label={intl.formatMessage({ id: "forms.resource.titleLabel" })}
        register={form.register("title")}
        required
        spellcheck
      />
      <RichTextarea<FormData>
        controls={[...standardRichTextareaMenuControls, "inAppLinks"]}
        editorRefresher={editorRefresher}
        error={form.formState.errors.description}
        form={form}
        journeyId={journey.id}
        label={intl.formatMessage({ id: "forms.resource.descriptionLabel" })}
        name="description"
        required
      />
      <div className={styles.urlInputs}>
        <div>
          <Input<FormData>
            error={form.formState.errors.linkTitle}
            label={intl.formatMessage({ id: "forms.resource.linkTitleLabel" })}
            register={form.register("linkTitle")}
            spellcheck
          />
        </div>
        <div>
          <Input<FormData>
            error={form.formState.errors.linkUrl}
            label={intl.formatMessage({ id: "forms.resource.linkUrlLabel" })}
            register={form.register("linkUrl")}
          />
        </div>
      </div>
      <div className={styles.phoneInputWrapper}>
        <PhoneInput<FormData>
          actions={[
            {
              ariaLabelKey: "accessibility.ariaLabels.clear",
              icon: "times",
              onClick: () => {
                form.setValue("phoneNumber", null);
                form.clearErrors("phoneNumber");
              }
            }
          ]}
          autofocus
          control={form.control}
          error={form.formState.errors.phoneNumber}
          label={intl.formatMessage({ id: "forms.resource.phoneNumberLabel" })}
          name="phoneNumber"
        />
      </div>
      <AsyncSelect<FormData>
        control={form.control}
        defaultOptions
        error={form.formState.errors.hotlineId}
        isClearable
        label={intl.formatMessage({ id: "forms.resource.hotlineIdLabel" })}
        name="hotlineId"
        onSearch={async search => hotlineAsyncSearch.searchOptions(search, getHotline({ variables: { search } }))}
        onSelection={(option: Option) => {
          hotlineAsyncSearch.setCurrentOption(option);
        }}
        value={hotlineAsyncSearch.currentOption}
      />
      <Input<FormData>
        error={form.formState.errors.cost}
        label={intl.formatMessage({ id: "forms.resource.costLabel" })}
        register={form.register("cost")}
      />

      {task.resourceFeatures.length > 0 && (
        <div className={styles.featuresSection}>
          <Heading3>
            <FormattedMessage id="forms.resource.featuresAndBenefits" />
          </Heading3>
          {resourceFeatureFields.map((field, index) => (
            <div key={field.id}>
              {task.resourceFeatures[index] && (
                <>
                  {task.resourceFeatures[index].kind === ResourceFeatureKindEnum.BOOLEAN ? (
                    <BooleanRadioButtonGroup<FormData>
                      control={form.control}
                      error={form.formState.errors.featureValues?.[index]?.value}
                      label={task.resourceFeatures[index].name}
                      name={`featureValues.${index}.value`}
                      options={optionsForBoolean(intl)}
                    />
                  ) : (
                    <RichTextarea<FormData>
                      controls={[...standardRichTextareaMenuControls, "inAppLinks"]}
                      editorRefresher={editorRefresher}
                      error={form.formState.errors.featureValues?.[index]?.value}
                      form={form}
                      label={task.resourceFeatures[index].name}
                      name={`featureValues.${index}.value`}
                    />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <FormButtons hookForm={form} onCancel={handleCancel} saveDisabled={!phoneNumberValid} />
    </Form>
  );
};

export default ResourceForm;
